import { FeatureSection } from "./FeatureSection";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import {
  Video,
  Users,
  Database,
  Building2,
  CheckCircle,
  Circle
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getBrandConfig } from "@/config/brand";

export const EnterpriseSection = () => {
  const { t } = useTranslation();
  const { ref: visualRef, isVisible: visualVisible } = useScrollAnimation({ threshold: 0.15 });
  const { brandNameShort } = getBrandConfig();

  return (
    <FeatureSection id="enterprise" background="muted">
      <div className="max-w-5xl mx-auto space-y-12">
        {/* Centered Copy */}
        <div className="text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400 text-xs font-medium">
            <Building2 className="h-3.5 w-3.5" />
            {t("landing.enterprise.badge")}
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
            {t("landing.enterprise.title")}{" "}
            <span className="text-gradient">{t("landing.enterprise.titleHighlight")}</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("landing.enterprise.description")}
          </p>
        </div>

        {/* Integration Flow Visual */}
        <div
          ref={visualRef}
          className={cn(
            "animate-scale-in",
            visualVisible && "is-visible"
          )}
        >
          <div className="relative py-8">
            {/* Connection Line - Hidden on mobile */}
            <div className="hidden lg:block absolute top-1/2 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-blue-500/50 via-primary to-green-500/50 -translate-y-1/2" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-4 items-center">
              {/* Input Sources */}
              <div className={cn(
                "space-y-3 animate-slide-left",
                visualVisible && "is-visible"
              )}>
                <div className="text-xs font-medium text-muted-foreground text-center lg:text-right uppercase tracking-wider mb-4">
                  {t("landing.enterprise.meetingPlatforms")}
                </div>
                <IntegrationCard icon={Video} name={t("landing.enterprise.integrations.zoom")} status="connected" />
                <IntegrationCard icon={Video} name={t("landing.enterprise.integrations.googleMeet")} status="coming" />
                <IntegrationCard icon={Users} name={t("landing.enterprise.integrations.microsoftTeams")} status="coming" />
              </div>

              {/* Central Hub */}
              <div className="flex justify-center">
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full" />

                  {/* Hub */}
                  <div className="relative w-32 h-32 rounded-full gradient-ai flex items-center justify-center shadow-2xl shadow-primary/30">
                    <div className="text-center text-white">
                      <div className="text-lg font-bold">{brandNameShort}</div>
                      <div className="text-[10px] opacity-80">{t("landing.enterprise.processing")}</div>
                    </div>
                  </div>

                  {/* Animated ring */}
                  <div className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" style={{ animationDuration: "2s" }} />
                </div>
              </div>

              {/* Output Destinations */}
              <div className={cn(
                "space-y-3 animate-slide-right",
                visualVisible && "is-visible"
              )}>
                <div className="text-xs font-medium text-muted-foreground text-center lg:text-left uppercase tracking-wider mb-4">
                  {t("landing.enterprise.enterpriseSystems")}
                </div>
                <IntegrationCard icon={Database} name={t("landing.enterprise.integrations.salesforce")} status="mock" align="left" />
                <IntegrationCard icon={Database} name={t("landing.enterprise.integrations.hubspot")} status="mock" align="left" />
                <IntegrationCard icon={Building2} name={t("landing.enterprise.integrations.sap")} status="mock" align="left" />
              </div>
            </div>
          </div>
        </div>

      </div>
    </FeatureSection>
  );
};

const IntegrationCard = ({
  icon: Icon,
  name,
  status,
  align = "right",
}: {
  icon: React.ElementType;
  name: string;
  status: "connected" | "available" | "coming" | "planned" | "mock";
  align?: "left" | "right";
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 rounded-xl bg-card border border-border shadow-sm",
      align === "right" ? "lg:flex-row-reverse lg:text-right" : ""
    )}>
      <div className={cn(
        "p-2.5 rounded-lg shrink-0",
        status === "connected" && "bg-green-500/10",
        status === "available" && "bg-blue-500/10",
        status === "mock" && "bg-amber-500/10",
        (status === "coming" || status === "planned") && "bg-muted"
      )}>
        <Icon className={cn(
          "h-5 w-5",
          status === "connected" && "text-green-500",
          status === "available" && "text-blue-500",
          status === "mock" && "text-amber-500",
          (status === "coming" || status === "planned") && "text-muted-foreground"
        )} />
      </div>
      <div className="flex-1">
        <div className="font-medium text-sm text-foreground">{name}</div>
        <div className="flex items-center gap-1.5 text-[10px] mt-0.5">
          {status === "connected" && (
            <>
              <CheckCircle className="h-3 w-3 text-green-500" />
              <span className="text-green-600 dark:text-green-400">{t("landing.enterprise.status.connected")}</span>
            </>
          )}
          {status === "available" && (
            <>
              <Circle className="h-3 w-3 text-blue-500" />
              <span className="text-blue-600 dark:text-blue-400">{t("landing.enterprise.status.available")}</span>
            </>
          )}
          {status === "coming" && (
            <>
              <Circle className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">{t("landing.enterprise.status.comingSoon")}</span>
            </>
          )}
          {status === "planned" && (
            <>
              <Circle className="h-3 w-3 text-amber-500" />
              <span className="text-amber-600 dark:text-amber-400">{t("landing.enterprise.status.planned")}</span>
            </>
          )}
          {status === "mock" && (
            <>
              <Circle className="h-3 w-3 text-amber-500" />
              <span className="text-amber-600 dark:text-amber-400">{t("landing.enterprise.status.mockDemo")}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
